import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/** Verfügbare Theme-Optionen. */
export type Theme = 'dark' | 'light' | 'system';

/** State und Aktionen des Theme Stores. */
export interface ThemeState {
  /** Aktuell gespeichertes Theme (dark, light oder system). */
  theme: Theme;
  /** Setzt das Theme und persistiert es in localStorage. */
  setTheme: (theme: Theme) => void;
}

/**
 * Zustand Store für die Theme-Verwaltung der Web-App.
 * Persistiert die Auswahl in localStorage unter dem Key "fanini-web-theme".
 * Bei "system" wird `prefers-color-scheme` des Browsers respektiert.
 *
 * @example
 * ```ts
 * const { theme, setTheme } = useThemeStore();
 * setTheme('dark');
 * ```
 */
export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      theme: 'system',
      setTheme: (theme) => set({ theme }),
    }),
    {
      name: 'fanini-web-theme',
    },
  ),
);

/**
 * Ermittelt das tatsächlich anzuwendende Theme (dark oder light).
 * Löst "system" anhand von `prefers-color-scheme` auf.
 *
 * @param theme - Das gespeicherte Theme (inkl. "system")
 * @returns "dark" oder "light"
 */
export function resolveTheme(theme: Theme): 'dark' | 'light' {
  if (theme === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return theme;
}
